import { useEffect, useRef } from 'react';
import { parseCopyDataSignal, tenantApi } from '@/services/tenant/tenant-api-config';
import { useCopyDataProgressStore } from '@/store/copyDataProgressStore';
import { logCopyDataTrace } from '@/utils/copyDataProgressSync';
import { COPY_DATA_STATUS_POLL_ENABLED } from '@/types/copy-data-progress';

const POLL_INTERVAL_MS = 5000;

/**
 * Poll copy-data status while a job is running (SignalR fallback).
 */
export function useCopyDataStatusPoll(): void {
  const phase = useCopyDataProgressStore((s) => s.phase);
  const jobId = useCopyDataProgressStore((s) => s.jobId);
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!COPY_DATA_STATUS_POLL_ENABLED) {
      return;
    }

    if (phase !== 'running' || !jobId || jobId.startsWith('local-')) {
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const poll = async () => {
      if (inFlightRef.current) return;
      // Job có thể đã complete giữa interval
      if (useCopyDataProgressStore.getState().phase !== 'running') return;

      inFlightRef.current = true;
      try {
        const response: unknown = await tenantApi.getCopyDataStatus({ jobId });
        // Bỏ qua nếu đã complete/failed trong lúc chờ response
        if (useCopyDataProgressStore.getState().phase !== 'running') return;

        const root = (response && typeof response === 'object' ? response : {}) as Record<
          string,
          unknown
        >;
        const data =
          root.data && typeof root.data === 'object'
            ? (root.data as Record<string, unknown>)
            : root;
        const phaseRaw = String(data.phase ?? data.Phase ?? 'progress').toLowerCase();
        if (phaseRaw === 'unknown') {
          logCopyDataTrace('status-poll-unknown', { jobId });
          return;
        }
        const signal = parseCopyDataSignal({
          title: 'Copy Data',
          body: phaseRaw,
          data: {
            ...data,
            jobId: data.jobId ?? data.JobId ?? jobId,
          },
        });
        if (signal) {
          logCopyDataTrace('status-poll', {
            phase: signal.phase,
            stepKey: signal.stepKey,
            percent: signal.percent,
          });
          useCopyDataProgressStore.getState().applyStatus(signal);
        }
      } catch (err) {
        logCopyDataTrace('status-poll-error', err);
      } finally {
        inFlightRef.current = false;
      }
    };

    void poll();
    timerRef.current = window.setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    return () => {
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [phase, jobId]);
}
